<template>
    <default-calculator-layout :filter-width="filterWidth" :output-width="outputWidth" :calcFilter="calcFilter" :calculator-title="calculatorTitle">
        <div slot="filter">
            <number-input v-model="CurrentMortgageBalance" :label="labelCurrentMortgageBalance" inputmode="decimal" />
            <number-input v-model="CurrentMonthlyPayment" :label="labelCurrentMonthlyPayment" inputmode="decimal" />
            <number-input v-model="NewInterestRate" :label="labelNewInterestRate" symbol="%" symbol-aligned="right" precision="3" :maxValue="maxVal" :tooltip-link="InterestRateTooltip" inputmode="decimal" />
            <drop-down v-model="LoanTermInYears" :label="labelLoanTermInYears" :options="LoanTermInYearsList" :tooltip-link="LoanTermTooltip" />
        </div>
        <div slot="output">
            <div class="loan-comparison">
                <table class="table table-border-none">
                    <tbody>
                        <tr>
                            <td width="70%">Consolidation loan amount:</td>
                            <td width="30%" class="payment text-right">{{ displayCurrentMortgageBalance }}</td>
                        </tr>
                        <tr>
                            <td>New Monthly Payment:</td>
                            <td class="payment text-right" :class="{ 'red-text': !differenceIncrOrDecr, 'green-text': differenceIncrOrDecr }">
                                {{ displayMonthlyPayment }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="analysis-results debt-consolidation-results">
                <ul>
                    <li>Your Monthly Savings Would Be: <span v-if="differenceIncrOrDecr!==0" class="result-amount" :class="{ 'green-text': differenceIncrOrDecr, 'red-text': !differenceIncrOrDecr}">{{ differenceMonthlyPayment }}</span></li>
                </ul>
            </div>
            <div class="compare-offers">
                <button class="btn btn-blue" type="submit" @click="redirectToUrl()">{{ DestinationButtonText }}</button>
            </div>
            <div class="disclosures">
                <advertising-disclosures v-if="DisclosureText" :disclosure-pre-text="disclosurePreText" :disclosure-text="DisclosureText" :disclosure-tokens="DisclosureTokens" />
            </div>
            <lt-logo v-if="ltLogo==1">
                <small>NMLS#1136 Terms & Conditions Apply</small>
            </lt-logo>
        </div>
    </default-calculator-layout>
</template>

<script>
    import Accounting from 'accounting';
    import PaymentHelper from '~/helpers/payment';
    import BaseMixin from '~/mixin/base-mixin';
    import LoanTermList from '~/constants/loan-term-list';
    import DefaultTooltip from '~/constants/tooltip-defaults';
    import LtLogo from '~/components/lt-logo.vue';
    import DefaultCalculatorLayout from '~/layout/default-calculator.vue';

    export default {
        props: {
            interestRateToken: {
                default: 'Personal_3Yr_5000_Rate',
            },
            labelCurrentMortgageBalance: {
                default: 'Loan Balance:',
            },
            labelCurrentMonthlyPayment: {
                default: 'Monthly Payment:',
            },
            labelNewInterestRate: {
                default: 'Consolidation Loan Rate:',
            },
            labelLoanTermInYears: {
                default: 'Loan Term:',
            },
            currentMortgageBalance: {
                default: 25000,
            },
            currentMonthlyPayment: {
                default: 1000,
            },
            newInterestRate: {
                default: 5.280,
            },
            loanTermInYears: {
                default: 5,
            },
            disclosureTokens: {
                default: '95',
            },
            disclosureText: {
                default: '',
            },
            disclosurePreText: {
                default: 'Monthly payments are an estimate and are for informational purposes. They do not represent a finance offer. Other tax, license, title, or fees may apply.',
            },
            interestRateTooltip: {
                default: DefaultTooltip.InterestRate,
            },
            loanTermTooltip: {
                default: DefaultTooltip.LoanTerm,
            },
            destinationLinkOrId: {
                default: 'wp-personal',
            },
            destinationButtonText: {
                default: 'Find Your Best Terms',
            },
            loanTermInYearsListType: {
                default: 'debtconsolidation',
            },
            ltLogo: {
                default: 0,
            },
            maxVal: {
                default: 100.000,
            },
        },
        data() {
            return {
                InterestRateToken: this.interestRateToken,
                CurrentMortgageBalance: this.currentMortgageBalance,
                CurrentMonthlyPayment: this.currentMonthlyPayment,
                NewInterestRate: this.newInterestRate,
                LoanTermInYears: this.loanTermInYears,
                LoanTermInYearsList: LoanTermList.LoanTermInYearsList[this.loanTermInYearsListType],
                InterestRateTooltip: parseInt(this.hideTooltip, 10) ? '' : this.interestRateTooltip,
                LoanTermTooltip: parseInt(this.hideTooltip, 10) ? '' : this.loanTermTooltip,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
                isUpdate: 0,
            };
        },
        methods: {
            monthlyPayment() {
                if (this.NewInterestRate > this.maxVal) {
                    this.$nextTick(() => {
                        this.NewInterestRate = this.maxVal;
                    });
                }
                return PaymentHelper.monthlyPaymentUsingLoanTermInYears(this.unformatFigure(this.CurrentMortgageBalance), this.LoanTermInYears, this.unformatFigure(this.NewInterestRate));
            },
            redirectToUrl() {
                this.DestinationLinkOrId = `${this.DestinationLinkOrId}&PersonalLoanPurposeCode=DEBTCONSOLIDATION`;
                this.redirect();
            },
        },
        computed: {
            displayCurrentMortgageBalance() {
                return Accounting.formatMoney(this.unformatFigure(this.CurrentMortgageBalance), { precision: 0 });
            },
            displayMonthlyPayment() {
                return Accounting.formatMoney(this.unformatFigure(this.monthlyPayment()), { precision: 0 });
            },
            differenceIncrOrDecr() {
                if (this.unformatFigure(this.CurrentMonthlyPayment) - this.unformatFigure(this.monthlyPayment()) === 0) {
                    return 0;
                }
                return this.unformatFigure(this.CurrentMonthlyPayment) - this.unformatFigure(this.monthlyPayment()) > 0;
            },
            differenceMonthlyPayment() {
                return Accounting.formatMoney(this.unformatFigure(this.CurrentMonthlyPayment) - this.unformatFigure(this.monthlyPayment()), { precision: 0 });
            },
        },
        watch: {
            NewInterestRate(newvalue, oldvalue) {
                if (this.isUpdate <= 1) {
                    if (newvalue !== oldvalue) {
                        this.NewInterestRate = Accounting.formatMoney(this.NewInterestRate, { symbol: '', precision: 3 });
                    }
                    this.isUpdate += 1;
                }
            },
        },
        components: {
            LtLogo,
            DefaultCalculatorLayout,
        },
        mixins: [BaseMixin],
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/analysis-comparisions-filter.scss" lang="scss" scoped></style>
